import type { Store } from "redux";

import type { AppState } from "src/store";
import { logOut } from "src/store/system/actions";
import {
  isImpersonationSessionSelector,
  userCompanySelector,
  userRoleSelector,
} from "src/store/system/selector";

import { ImpersonationRevokedReason } from "src/utils/callableErrors";
import {
  buildImpersonationBounceUrl,
  ImpersonationBounceReason,
} from "src/utils/impersonationRevocationUrl";

/** Exact middleware message when the Tokens grant was deleted (revoke). */
export const IMPERSONATION_REVOKED_MESSAGE =
  "Your access to this account has been revoked.";

let handling = false;

export function isImpersonationRevokedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const err = error as { code?: string; message?: string; details?: { reason?: string } };
  if (err.details?.reason === ImpersonationRevokedReason) return true;
  const code = err.code || "";
  const message = err.message || "";
  const isDenied =
    code === "permission-denied" ||
    code === "functions/permission-denied" ||
    /permission-denied/i.test(code);
  return isDenied && message.includes("has been revoked");
}

/**
 * Sign out of the impersonated reseller session and hard-navigate away.
 * Used when the Tokens grant disappears (revoke) or a callable returns revoked.
 */
export function bounceImpersonationSession(store: Store<AppState>): boolean {
  const state = store.getState();
  if (!isImpersonationSessionSelector(state)) return false;
  if (handling) return true;
  handling = true;

  const role = userRoleSelector(state);
  const accountName = userCompanySelector(state)?.name;

  if (role === "accountant") {
    store.dispatch(logOut());
    window.location.assign(
      buildImpersonationBounceUrl(
        "/accountant",
        ImpersonationBounceReason.AccessRevoked,
        accountName
      )
    );
    return true;
  }

  if (role === "admin" || role === "viewer") {
    store.dispatch(logOut());
    window.location.assign(
      buildImpersonationBounceUrl(
        "/support",
        ImpersonationBounceReason.AccessEnded,
        accountName
      )
    );
    return true;
  }

  store.dispatch(logOut());
  window.location.assign(
    buildImpersonationBounceUrl(
      "/login",
      ImpersonationBounceReason.AccessEnded,
      accountName
    )
  );
  return true;
}

/**
 * If this error is a live revoke while impersonating an accountant session,
 * sign out of the reseller Firebase session and send them back to My Clients.
 * Returns true when a bounce was started (caller can still rethrow).
 */
export function handleImpersonationRevokedError(
  error: unknown,
  store: Store<AppState>
): boolean {
  if (!isImpersonationRevokedError(error)) return false;
  const state = store.getState();
  if (!isImpersonationSessionSelector(state)) return false;
  if (userRoleSelector(state) !== "accountant") return false;
  return bounceImpersonationSession(store);
}
